import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid2,
  Link,
  Stack,
  Typography,
  useTheme,
  LinearProgress,
  CardMedia,
  alpha,
} from "@mui/material";

import {
  ChipParticipant,
  DetailsTodo,
  InvestigationCollapsable,
  Separator,
} from "../components/InvestigationComponents";
import { InvestigationButton } from "../components/InvestigationButton";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { EGSwitch } from "../../../../MainComponents/Switch/Switch";
import EGDatePicker from "../../../../MainComponents/DatePicker/EGDatePicker";
import { InvestigationItemContainer } from "../components/InvestigationItemContainer";
import { AddResourceForActivity } from "./Modals/AddResourceForActivity";
import { AddTodoForActivity } from "./Modals/AddTodoForActivity";
import { activitySchema } from "./schemas";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { returnIsValid, useConstants } from "../Main/useConstants";
import { SectionedSelect } from "../../../../MainComponents/Select/SectionedSelect";
import { GenerateWithIA } from "../components/InvestigationComponents";
import { useReport } from "../../../../../context/report/ReportContext";
import { SuggestionPlanAI } from "./SuggestionPlanAI";

const validateForm = (form) => {
  const errors = {};

  if (!form.name) {
    errors.name = true;
  }

  if (form.assignedTo?.id === "" || form.assignedTo?.from === "") {
    errors.assignedTo = true;
  }

  return [Object.entries(errors).length === 0 ? true : false, errors];
};

export const AddAndEditPlan = ({
  plans,
  selectedActivity,
  setSelectedActivity,
  setIsEditing,
  handleSavePlans,
  investigation,
  setInvestigation,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const {
    enums,
    functions: { getResourceLabel, getUserLabel },
    plans: { statusOpts },
    selects: { hoursOpts, typeUserLabels },
  } = useConstants();

  // email/username se mantienen desde redux auth
  const { email, username } = useSelector((state) => state.auth);

  // Usamos el contexto en lugar de state.jaguar
  const { state } = useReport();

  // obtén recursos aprobados desde reportState de forma segura (fallbacks)
  const inv = state?.currentReport?.investigation ?? {};
  const accessResourcesInfo = inv?.accessResourcesInfo ?? {
    approved: [],
    approvedFormatted: [],
  };
  const financialResourcesInfo = inv?.financialResourcesInfo ?? {
    approved: [],
    approvedFormatted: [],
  };
  const materialResourcesInfo = inv?.materialResourcesInfo ?? {
    approved: [],
    approvedFormatted: [],
  };

  const [activity, setActivity] = useState({
    ..._.cloneDeep(activitySchema),
    assignedTo: { id: email, from: enums.typeUser.ME },
  });

  const [party, setParty] = useState([]);

  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [todos, setTodos] = useState([]);
  const [selectedTodo, setSelectedTodo] = useState(null);

  const [errors, setErrors] = useState({});
  const [triedToSave, setTriedToSave] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [newParticipant, setNewParticipant] = useState(null);

  const [activeDetails, setActiveDetails] = useState(false);
  const [activeDeadline, setActiveDeadline] = useState(false);
  const [showAddResource, setShowAddResource] = useState(false);
  const [showAddTodo, setShowAddTodo] = useState(false);

  // For IA Generator
  const [iaGenerator, setIaGenerator] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateAIContent = () => {
    // Simular llamada a API que toma entre 30s y 2 minutos
    return new Promise((resolve) => {
      const randomTime = Math.random() * (120000 - 30000) + 30000; // 30s a 2min
      setTimeout(resolve, randomTime);
    });
  };

  // Select for assign the activity

  const assignedToSections = useMemo(() => {
    const { ME, RESOURCES, EXTERNAL, SYSTEM } = enums.typeUser;
    const usersWanted = [ME, RESOURCES, EXTERNAL, SYSTEM];

    let newSections = [];

    usersWanted.forEach((value) => {
      if (value === ME) {
        newSections.push({
          key: value,
          options: [
            {
              value: email,
              label: username || email || t("activityPlan.assign_to_me"),
            },
          ],
        });
        return;
      }
      const path = _.get(enums, ["typeUserRoutes", value]);
      const node = _.get(state, path);
      const fromArray =
        (node &&
          node.formatted &&
          Array.isArray(node.formatted) &&
          node.formatted) ||
        (node && node.raw && Array.isArray(node.raw) && node.raw) ||
        (Array.isArray(node) && node) ||
        [];

      newSections.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    // Disable party from selections
    party.forEach((participant) => {
      const { id, from } = participant ?? {};
      const findedSectionIdx = newSections.findIndex(
        (section) => section.key === from
      );
      if (findedSectionIdx !== -1) {
        newSections[findedSectionIdx].options = newSections[
          findedSectionIdx
        ].options.map((option) => {
          if (option.value === id) {
            return { ...option, disabled: true };
          }
          return option;
        });
      }
    });

    return newSections;
  }, [party, state, enums, typeUserLabels, email, username]);

  // Select for add participants
  const participantsSections = useMemo(() => {
    const { ME, RESOURCES, EXTERNAL, SYSTEM } = enums.typeUser;
    const usersWanted = [ME, RESOURCES, EXTERNAL, SYSTEM];

    let newSections = [];

    usersWanted.forEach((value) => {
      if (value === ME) {
        newSections.push({
          key: value,
          options: [
            {
              value: email,
              label: username || email || t("activityPlan.participate_in_the_activity"),
            },
          ],
        });
        return;
      }
      const path = _.get(enums, ["typeUserRoutes", value]);
      const node = _.get(state, path);
      const fromArray =
        (node &&
          node.formatted &&
          Array.isArray(node.formatted) &&
          node.formatted) ||
        (node && node.raw && Array.isArray(node.raw) && node.raw) ||
        (Array.isArray(node) && node) ||
        [];

      newSections.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    // Kick assigned from selections
    const { id: assignedId, from: assignedFrom } = activity?.assignedTo ?? {};
    const findedSectionIdx = newSections.findIndex(
      (section) => section.key === assignedFrom
    );
    if (findedSectionIdx !== -1) {
      newSections[findedSectionIdx].options = newSections[
        findedSectionIdx
      ].options.map((option) => {
        if (option.value === assignedId) {
          return { ...option, disabled: true };
        }
        return option;
      });
    }

    // Disable party from selections
    party.forEach((participant) => {
      const { id, from } = participant ?? {};
      const findedSectionIdx2 = newSections.findIndex(
        (section) => section.key === from
      );
      if (findedSectionIdx2 !== -1) {
        newSections[findedSectionIdx2].options = newSections[
          findedSectionIdx2
        ].options.map((option) => {
          if (option.value === id) {
            return { ...option, disabled: true };
          }
          return option;
        });
      }
    });

    return newSections;
  }, [activity.assignedTo, party, state, enums, typeUserLabels, email, username]);

  const existsResources = useMemo(() => {
    return (
      (accessResourcesInfo?.approved?.length || 0) > 0 ||
      (financialResourcesInfo?.approved?.length || 0) > 0 ||
      (materialResourcesInfo?.approved?.length || 0) > 0
    );
  }, [accessResourcesInfo, financialResourcesInfo, materialResourcesInfo]);

  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm(activity);
      setErrors(errors);
    }
  }, [activity.name]);

  useEffect(() => {
    return () => {
      resetValues();
    };
  }, []);

  useEffect(() => {
    if (selectedActivity != null) {
      setActivity(plans[selectedActivity]);
      setParty(plans[selectedActivity].party || []);
      setResources(plans[selectedActivity].resources || []);
      setTodos(plans[selectedActivity].todos || []);
      setActiveDetails(plans[selectedActivity].details ? true : false);
      setActiveDeadline(plans[selectedActivity].deadline ? true : false);
    }
  }, [selectedActivity]);

  const handleSave = async () => {
    const [isValid, errors] = validateForm(activity);
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      setTriedToSave(false);
      enqueueSnackbar({
        variant: "error",
        message: t("resources.missing_required_inputs"),
      });
      return;
    }

    setIsSaving(true);
    const savingSnackbarKey = enqueueSnackbar({
      variant: "info",
      message: t("activityPlan.saving"),
      persist: true,
    });
    try {
      if (selectedActivity == null) {
        const newPlans = _.cloneDeep(plans);
        newPlans.push({
          ...activity,
          party,
          resources,
          todos,
          created_at: dayjs().toISOString(),
        });
        await handleSavePlans(newPlans);
      } else {
        const newPlans = _.cloneDeep(plans);
        newPlans[selectedActivity] = {
          ...activity,
          party,
          resources,
          todos,
        };
        await handleSavePlans(newPlans);
      }
    } finally {
      closeSnackbar(savingSnackbarKey);
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedActivity(null);
    setIsEditing(false);
  };

  const handleChange = (e) => {
    if (
      e.target.name === "status" &&
      e.target.value !== "completed" &&
      activity.status === "completed" &&
      dayjs(activity.deadline).isBefore(dayjs())
    ) {
      setActivity({
        ...activity,
        [e.target.name]: e.target.value,
        deadline: "",
        timeDeadline: "",
      });
      return;
    }
    setActivity({ ...activity, [e.target.name]: e.target.value });
  };

  const handleChangeResponsible = ({ value, section, label }) => {
    setNewParticipant(null);
    setActivity({ ...activity, assignedTo: { id: value, from: section, label } });
  };

  const resetValues = () => {
    setActivity(_.cloneDeep(activitySchema));
    setErrors({});
    setTriedToSave(false);
    setActiveDetails(false);
    setActiveDeadline(false);
  };

  // Party -----------------------------------------------------------------
  const handleAddParty = () => {
    setParty([...party, newParticipant]);
    setNewParticipant(null);
  };

  const handleDeleteParty = (idx) => {
    const newParty = _.cloneDeep(party);
    newParty.splice(idx, 1);
    setParty(newParty);
  };

  // Resources -------------------------------------------------------------
  const handleEditResource = (idx) => {
    setSelectedResource(idx);
    setShowAddResource(true);
  };

  const handleDeleteResource = (idx) => {
    const newResources = _.cloneDeep(resources);
    newResources.splice(idx, 1);
    setResources(newResources);
  };

  // Tasks ----------------------------------------------------------------
  const handleEditTodo = (idx) => {
    setSelectedTodo(idx);
    setShowAddTodo(true);
  };

  const handleDeleteTodo = (idx) => {
    const newTodos = _.cloneDeep(todos);
    newTodos.splice(idx, 1);
    setTodos(newTodos);
  };

  return (
    <>
      <AddResourceForActivity
        show={showAddResource}
        onHide={() => setShowAddResource(false)}
        resources={resources}
        setResources={setResources}
        selectedResource={selectedResource}
        setSelectedResource={setSelectedResource}
        investigation={investigation}
        setInvestigation={setInvestigation}
      />

      <AddTodoForActivity
        show={showAddTodo}
        onHide={() => setShowAddTodo(false)}
        todos={todos}
        setTodos={setTodos}
        selectedTodo={selectedTodo}
        setSelectedTodo={setSelectedTodo}
      />

      {/* AI Activity Plan Suggestions */}
      <SuggestionPlanAI
        open={iaGenerator}
        onClose={() => setIaGenerator(false)}
        t={t}
        alias={investigation.alias}
        plans={plans}
        setActivity={setActivity}
        setTodos={setTodos}
        setParty={setParty}
        setIsGenerating={setIsGenerating}
        setIsEditing={setIsEditing}
      />

      <Box padding={2}>
        {/* Title */}
        <Box sx={{ width: "100%", display: "inline-block", p: "1em 0em" }}>
          <GenerateWithIA
            text={t("activityPlan.generate_with_ai")}
            buttonText={t("activityPlan.generate_with_ai_button")}
            onClick={() => setIaGenerator(true)}
            style={{ float: "right", marginTop: "-1em", marginRight: "0px" }}
            isLoading={triedToSave || isGenerating}
          />
          <Separator
            text={t("activityPlan.add_new_activity_plan")}
            sx={{ mb: 2, width: "auto" }}
          />
        </Box>

        {!iaGenerator && (
          <>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <SectionedSelect
                  label={`${t("activityPlan.responsible_of_the_activity")}:`}
                  name="assignedTo"
                  value={activity.assignedTo?.id ?? ""}
                  onChange={handleChangeResponsible}
                  sections={assignedToSections}
                  required={true}
                  isValid={returnIsValid(errors?.assignedTo, triedToSave) ?? null}
                  showSuccessValidation
                  showHelpText={false}
                  disabled={isSaving}
                  fallbackLabel={getUserLabel(activity.assignedTo)}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 6 }}>
                <EGInput
                  label={`${t("activityPlan.name_of_the_activity")}:`}
                  name="name"
                  value={activity.name}
                  onChange={handleChange}
                  required={true}
                  isValid={returnIsValid(errors?.name, triedToSave) ?? null}
                  showSuccessValidation
                  showHelpText={false}
                  disabled={isSaving}
                />
                {!activeDetails && (
                  <Box display="flex" justifyContent="flex-end" mt={0.5}>
                    <Link
                      onClick={() => setActiveDetails(true)}
                      underline="hover"
                    >
                      + {t("activityPlan.add_details_to_the_activity")}
                    </Link>
                  </Box>
                )}
              </Grid2>

              {activeDetails && (
                <Grid2 size={12}  >
                  <EGInput
                    label={`${t("activityPlan.details_of_the_activity")}`}
                    name="details"
                    value={activity.details}
                    onChange={handleChange}
                    type="textarea"
                    multiline
                    rows={3}
                    disabled={isSaving}
                  />
                </Grid2>
              )}

              <Grid2 size={{ xs: 12, md: 6 }} >
                <EGBasicSelect
                  label={t("activityPlan.status")}
                  name="status"
                  value={activity.status}
                  onChange={handleChange}
                  options={statusOpts}
                  disabled={isSaving}
                />
              </Grid2>

              <Grid2
                size={{ xs: 12, md: 6 }}
                container
                spacing={2}
              >
                <Grid2 size={9}>
                  <SectionedSelect
                    label={t("activityPlan.participants")}
                    name="party"
                    value={newParticipant?.id ?? ""}
                    onChange={({ value, section, label }) =>
                      setNewParticipant({ id: value, from: section, label })
                    }
                    sections={participantsSections}
                    disabled={isSaving}
                  />
                </Grid2>
                <Grid2 size={3}>
                  <InvestigationButton
                    label={`${t("activityPlan.add_alone")}`}
                    icon={false}
                    size="medium"
                    onClick={handleAddParty}
                    sx={{ width: "100%" }}
                    disabled={!newParticipant || isSaving}
                  />
                </Grid2>
              </Grid2>

              {party.length > 0 && (
                <Grid2 size={12}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {party.map((participant, idx) => (
                      <ChipParticipant
                        key={idx}
                        idx={idx}
                        role={
                          typeof participant === "string"
                            ? t("activityPlan.participant")
                            : typeUserLabels[participant.from]
                        }
                        name={
                          typeof participant === "string"
                            ? participant
                            : getUserLabel(participant) || t("activityPlan.user_not_found")
                        }
                        handleDeleteParticipant={() => handleDeleteParty(idx)}
                      />
                    ))}
                  </Stack>
                </Grid2>
              )}

              <Grid2
                size={{ xs: 12, md: 6 }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  height={"100%"}
                >
                  <Typography
                    fontSize={16}
                    fontWeight={600}
                    color={theme.palette.secondary.main}
                  >
                    {t("activityPlan.set_deadline")}
                  </Typography>
                  <EGSwitch
                    checked={activeDeadline}
                    onChange={() => setActiveDeadline(!activeDeadline)}
                    size="small"
                    sx={{ marginRight: 0.5 }}
                    disabled={isSaving}
                  />
                </Box>
              </Grid2>

              <Grid2
                size={{ xs: 12, md: 6 }}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                {activeDeadline && (
                  <>
                    <EGDatePicker
                      label={`${t("activityPlan.deadline")}`}
                      name="deadline"
                      value={activity.deadline ? dayjs(activity.deadline) : null}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "deadline", value: e.toISOString() },
                        })
                      }
                      className="date-picker-deadline"
                      format="DD/MMM/YYYY"
                      minDate={activity.status === "completed" ? null : dayjs()}
                      disabled={isSaving}
                    />

                <Box width={140}>
                  <EGBasicSelect
                    label={t("activityPlan.time")}
                    name="timeDeadline"
                    value={activity.timeDeadline}
                    onChange={handleChange}
                    options={hoursOpts}
                    disabled={isSaving}
                  />
                </Box>
              </>
            )}
          </Grid2>
        </Grid2>
        {/* Activity actions */}
        <Stack
          spacing={2}
          p={2}
          mt={2}
          borderRadius="5px"
          border={`1px solid ${
            returnIsValid(errors?.interview) == null
              ? theme.palette.primary.main
              : returnIsValid(errors?.interview)
              ? theme.palette.green.main
              : theme.palette.red.main
          }`}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <InvestigationItemContainer
              color={theme.palette.primary.main}
              width="fit-content"
              sx={{ padding: 0.8 }}
            >
              <Typography fontSize={13} fontWeight={600}>
                {t("activityPlan.actions_of_the_activity")}
              </Typography>
            </InvestigationItemContainer>

            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="flex-end"
              spacing={1}
            >
              <InvestigationButton
                label={`${t("activityPlan.assign_resource")}`}
                icon="outline_data_setting"
                size="medium"
                onClick={() => setShowAddResource(true)}
                disabled={!existsResources || isSaving}
              />
              <InvestigationButton
                label={`${t("activityPlan.add_task")}`}
                size="medium"
                icon="add_circle"
                onClick={() => setShowAddTodo(true)}
                disabled={isSaving}
              />
            </Stack>
          </Stack>
          {/* Resources and tasks */}
          {resources.length > 0 || todos.length > 0 ? (
            <hr className="hr-div" style={{ margin: "0", opacity: 1 }} />
          ) : (
            <Box
              border={`2px solid ${theme.palette.pink.main}`}
              borderRadius={"10px"}
              px={2}
              py={1.5}
            >
              <Typography fontSize={16} color="oxfordBlue.main">
                {t("activityPlan.assign_resources_to_the_activity")}
              </Typography>
            </Box>
          )}

          {
            // Resources
            resources.length > 0 && (
              <>
                <Separator text={t("activityPlan.assigned_resources")} />
                {resources.map((resource, idx) => (
                  <InvestigationCollapsable
                    key={idx}
                    title={
                      <Typography
                        color="secondary.main"
                        fontSize={14}
                        fontWeight={600}
                      >
                        {resource.type === "access"
                          ? t("resources.access_resource")
                          : resource.type === "financial"
                          ? t("resources.financial_resource")
                          : resource.type === "material"
                          ? t("resources.material_resource")
                          : t("")}
                        :&nbsp;
                        <Typography
                          component="span"
                          fontSize={16}
                          fontWeight={700}
                        >
                          {getResourceLabel(resource)}
                        </Typography>
                      </Typography>
                    }
                    details={
                      resource.details && (
                        <Typography
                          color="blueGray600.main"
                          fontSize={11}
                          fontWeight={600}
                        >
                          {t("activityPlan.answer")}:&nbsp;
                          <Typography
                            color="oxfordBlue.main"
                            component="span"
                            fontSize={11}
                            fontStyle="italic"
                          >
                            {resource.details}
                          </Typography>
                        </Typography>
                      )
                    }
                    buttons={[
                      {
                        tooltip: t("activityPlan.delete"),
                        name: "trash_can",
                        size: 20,
                        color: theme.palette.red.main,
                        onClick: () => handleDeleteResource(idx),
                      },
                      {
                        tooltip: t("activityPlan.edit"),
                        name: "outline_edit",
                        size: 20,
                        color: theme.palette.primary.main,
                        onClick: () => handleEditResource(idx),
                      },
                    ]}
                  />
                ))}
              </>
            )
          }
          {
            // Tasks
            todos.length > 0 && (
              <>
                <Separator text={t("activityPlan.assigned_tasks")} />
                {todos.map((todo, idx) => (
                  <InvestigationCollapsable
                    key={idx}
                    title={
                      <Typography color="secondary.main" fontSize={14}>
                        {t("activityPlan.task")}{" "}
                        {todo.completed
                          ? t("activityPlan.completed")
                          : t("activityPlan.pending").toLocaleLowerCase()}
                        :&nbsp;
                        <Typography
                          component="span"
                          fontSize={16}
                          fontWeight={700}
                        >
                          {typeof todo === "string" ? todo : todo.name}
                        </Typography>
                      </Typography>
                    }
                    details={
                      (todo.assignedTo?.id ||
                        todo.details ||
                        todo.deadline ||
                        todo.dedicatedHours ||
                        todo.party?.length > 0) && <DetailsTodo todo={todo} />
                    }
                    buttons={[
                      {
                        tooltip: t("activityPlan.delete"),
                        name: "trash_can",
                        size: 20,
                        color: theme.palette.red.main,
                        onClick: () => handleDeleteTodo(idx),
                      },
                      {
                        tooltip: t("activityPlan.edit"),
                        name: "outline_edit",
                        size: 20,
                        color: theme.palette.primary.main,
                        onClick: () => handleEditTodo(idx),
                      },
                    ]}
                  />
                ))}
              </>
            )
          }
        </Stack>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="flex-end"
          spacing={1}
          mt={2}
        >
          <InvestigationButton
            label={`${t("causes.cancel")}`}
            icon="close_icon"
            size="medium"
            onClick={handleClose}
            disabled={isSaving || isGenerating}
          />
          <InvestigationButton
            label={`${t("activityPlan.save")}`}
            size="medium"
            variant="success"
            icon="mark_check_bold"
            onClick={handleSave}
            loading={isSaving}
            disabled={isSaving || isGenerating}
          />
        </Stack>
          </>
        )}
      </Box>
    </>
  );
};
